export interface NotificationTexts {
  lp: {
    sectionSuccess: string;
    sectionError: string;
  };
}
import { ProgressProps } from 'antd';

export const twoColors: ProgressProps['strokeColor'] = {
  '0%': '#7749D9',
  '100%': '#A64AD9',
};

export const notificationTexts: NotificationTexts = {
  lp: {
    sectionSuccess: 'Section Saved Successfully',
    sectionError: 'Section Saved Error',
  },
};
export enum LandingTemplateStatuses {
  none = 'none',
  waiting_for_approval = 'waiting_for_approval',
  approved = 'approved',
  rejected = 'rejected',
}

export enum SetupLandingStatuses {
  none = 'none',
  waiting_for_approval = 'waiting_for_approval',
  approved = 'approved',
  rejected = 'rejected',
}

export enum CampaignStatuses {
  draft = 'draft',
  enabled = 'enabled',
  paused = 'paused',
  archived = 'archived',
  completed = 'completed',
  error = 'error',
}

/** CampaignAdStatus case names — the computed status (on_review / rejected fold in moderation). */
export enum CampaignAdStatuses {
  draft = 'draft',
  enabled = 'enabled',
  paused = 'paused',
  archived = 'archived',
  rejected = 'rejected',
  on_review = 'on_review',
}

export enum WithdrawalStatuses {
  completed = 'completed',
  requested = 'requested',
  in_progress = 'in_progress',
  cancelled = 'cancelled',
}

export enum EventCatalogItemStatuses {
  invitation_pending = 'invitation_pending',
  available = 'available',
  on_review = 'on_review',
  cancelled = 'cancelled',
  rejected = 'rejected',
  active = 'active',
  suspended = 'suspended',
  archived = 'archived',
}

export enum EventStatuses {
  draft = 'draft',
  published = 'published',
}

export enum ModerationStatuses {
  draft = 'draft',
  published = 'published',
  on_review = 'on_review',
  rejected = 'rejected',
  under_appeal = 'under_appeal',
}

export const PUBLISH_WITHOUT_MODERATION_TRANSIT = 'Publish without moderation';
export const PUBLISH_WITHOUT_MODERATION_TRANSITION = 6;

export enum CredentialsStatuses {
  none = 'none',
  enabled = 'enabled',
  revoked = 'revoked',
  archived = 'archived',
}

export type ModerationStatus = 'draft' | 'published' | 'on_review' | 'rejected' | 'under_appeal';

export enum CreativeStatuses {
  on_moderation = 'on_moderation',
  approved = 'approved',
  rejected = 'rejected',
}

export enum SnippetStatuses {
  enabled = 'enabled',
  disabled = 'disabled',
  archived = 'archived',
}

// status of one creative asset (image, logo, video, copy). Replacing a media
// asset disables the one it replaced instead of deleting it, so a creative can
// carry both — read asset arrays through lib.shared/src/creativeAssets
export enum CreativeAssetStatuses {
  enabled = 'enabled',
  disabled = 'disabled',
}

export enum ArtifactTypes {
  media_plan = 'media_plan',
  creative = 'creative',
  creative_template = 'creative_template',
  landing_template = 'landing_template',
  audience_segment = 'audience_segment',
  report = 'report',
  video = 'video',
  image = 'image',
  creative_scenario = 'creative_scenario',
  creative_reference = 'creative_reference',
  creative_generative_scenario = 'creative_generative_scenario',
}

// the Creative Library types a creative idea may be implemented by (mirrors
// backend ArtifactType::creativeLibraryTypes); numeric ids come from the
// artifact_type enum dictionary
export const CREATIVE_LIBRARY_TYPE_SLUGS: string[] = [
  ArtifactTypes.creative_scenario,
  ArtifactTypes.creative_reference,
  ArtifactTypes.creative_generative_scenario,
];

// creative idea / idea category statuses by slug; numeric ids come from the
// API dictionaries (creative_idea_status, creative_idea_category_status)
export enum CreativeIdeaStatuses {
  review = 'review',
  enabled = 'enabled',
  disabled = 'disabled',
}

/** EventCreativeIdeaMode slugs — how an event draws on the creative ideas catalog. */
export enum EventCreativeIdeaModes {
  catalog = 'catalog',
  curated = 'curated',
}

/** CreativeIdeaScope slugs — the global catalog, or the ideas made for single events. */
export enum CreativeIdeaScopes {
  global = 'global',
  event = 'event',
}

/** EventCreativeIdeaStatus slugs — the event's switch on one catalog idea. */
export enum EventCreativeIdeaStatuses {
  enabled = 'enabled',
  disabled = 'disabled',
}

/** PremadeCreativeStatus slugs — one admin-made creative of an idea for one company. */
export enum PremadeCreativeStatuses {
  generating = 'generating',
  review = 'review',
  approved = 'approved',
  rejected = 'rejected',
  failed = 'failed',
}

/** PremadeCreativeSource slugs — who produced the creative. */
export enum PremadeCreativeSources {
  agent = 'agent',
  mcp = 'mcp',
  logo_swap = 'logo_swap',
}

// agent type slugs (string-backed on the API — safe to reference by slug)
export enum AgentTypes {
  match_concierge = 'match_concierge',
}

// match card statuses by slug; their numeric ids come from the API dictionary
// (`partner_match_status`) — see useMatchStatusIds
export enum MatchStatuses {
  suggested = 'suggested',
  liked = 'liked',
  passed = 'passed',
  meeting_requested = 'meeting_requested',
}

export const HELP_TRACKING_CODE_LINK =
  'https://help.boost.express/knowledge-base/add-boost.express-tracking-code-to-your-site';
export const HELP_SUBDOMAIN_LINK =
  'https://help.boost.express/knowledge-base/setting-up-a-subdomain-for-landing-pages';
export const HELP_CENTER_LINK = 'https://help.boost.express/knowledge-base';

/**
 * The platform film (Danis, 10.2026) on the CDN's onboarding videos — the desk's
 * welcome and the header's "First overview of the platform" play it. A new cut
 * goes up under a new name or with a `?v2` here: the edge caches the old copy
 * for an hour.
 */
export const ONBOARDING_FILM_URL =
  'https://cdn.boost.express/videos/onboarding/how-your-campaign-comes-together-1080p.mp4';

/** The film's opening scene, a still beside it — the film itself fades in from white. */
export const ONBOARDING_FILM_POSTER_URL =
  'https://cdn.boost.express/videos/onboarding/how-your-campaign-comes-together-poster.jpg';

/**
 * The film's English captions (CEO doc Debug (4), D5-04). A <track> from the
 * CDN is a cross-origin read: the bucket answers GET with CORS (set 09.10.2026)
 * and the film's <video> asks with crossOrigin="anonymous". `?v1` skips any
 * edge copy cached before CORS — bump it when the captions change.
 */
export const ONBOARDING_FILM_CAPTIONS_URL =
  'https://cdn.boost.express/videos/onboarding/how-your-campaign-comes-together-captions.vtt?v1';

export const HELP_CENTER_LINK_EVENTS = 'https://help.boost.express/knowledge-base/organizers';
export const HELP_CENTER_LINK_SPONSORS = 'https://help.boost.express/knowledge-base/sponsors';
export const PDF_LINK =
  'https://help.boost.express/knowledge-base/setting-up-a-subdomain-for-landing-pages';
export const HELP_CENTER_LINK_BILLING =
  'https://help.boost.express/knowledge-base/managing-billing-and-payments#faq';
export const GAP_INIT = '1';

export const INIT_CURRENT_PAGE = 1;
export const INIT_PAGE_SIZE = 15;
export const INIT_TOTAL = 15;

export const Orders: Record<string, string> = {
  descend: 'desc',
  ascend: 'asc',
};
